import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@stadium/services/apollo-client";
import { FETCH_PAGINATED_DRAFT_CATALOG_PRODUCTS } from "@graphql/queries/stores/catalog/fetch-paginated-draft-catalog-products";

const DEFAULT_PER_PAGE = 30;

const serializeDraftCatalogProducts = (products = []) => {
  return products.map((product) => ({
    ...product,
    productId: Number(product?.id),
    baseImage: product?.images?.[0],
    brand: product?.brand?.name ? { displayName: product.brand.name } : product?.brand,
  }));
};

const EMPTY_META = {
  total: 0,
  countryWiseProductCount: {},
  globalProductCount: 0,
  totalColorOptions: 0,
  totalProductsCount: 0,
};

const useFetchPaginatedDraftCatalogProducts = ({
  catalogNumber,
  setProducts,
  setMeta,
  perPage = DEFAULT_PER_PAGE,
  countryIso = "",
  fetchAll = false,
  defaultHasMore = false,
} = {}) => {
  const [fetchQuery] = useLazyQuery(FETCH_PAGINATED_DRAFT_CATALOG_PRODUCTS, {
    fetchPolicy: "network-only",
  });

  const [hasMore, setHasMore] = useState(defaultHasMore);
  const [isFetchingFirstPage, setIsFetchingFirstPage] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const pageRef = useRef(0);

  useEffect(() => {
    setHasMore(defaultHasMore);
  }, [defaultHasMore]);

  const fetchPage = useCallback(
    async (page) => {
      const { data } = await fetchQuery({
        variables: {
          number: catalogNumber,
          page,
          perPage,
          iso: countryIso,
          ...(fetchAll && { all: true }),
        },
      });
      const payload = data?.paginatedDraftCatalogProducts;
      return {
        total: payload?.total || 0,
        countryWiseProductCount: payload?.countryWiseProductCount || {},
        globalProductCount: payload?.globalProductCount || 0,
        totalColorOptions: payload?.totalColorOptions || 0,
        totalProductsCount: payload?.totalProductsCount || 0,
        products: serializeDraftCatalogProducts(payload?.products || []),
      };
    },
    [fetchQuery, catalogNumber, perPage, countryIso],
  );

  useEffect(() => {
    if (!catalogNumber || !setProducts) return;

    pageRef.current = 0;
    setHasMore(false);

    setIsFetchingFirstPage(true);
    fetchPage(1)
      .then(
        ({
          products,
          total,
          countryWiseProductCount,
          globalProductCount,
          totalColorOptions,
          totalProductsCount,
        }) => {
          setProducts(products);
          setMeta?.({
            total,
            countryWiseProductCount,
            globalProductCount,
            totalColorOptions,
            totalProductsCount,
          });
          pageRef.current = 1;
          setHasMore(products.length < total);
        },
      )
      .catch(() => {
        setProducts([]);
        setMeta?.(EMPTY_META);
      })
      .finally(() => setIsFetchingFirstPage(false));
  }, [catalogNumber, countryIso]);

  const fetchNextPage = useCallback(() => {
    if (isFetchingFirstPage || isFetchingNextPage || !hasMore || !catalogNumber) return;

    const nextPage = pageRef.current + 1;
    setIsFetchingNextPage(true);
    fetchPage(nextPage)
      .then(({ products, total }) => {
        if (products.length === 0) {
          setHasMore(false);
          return;
        }
        setProducts((prev) => [...prev, ...products]);
        pageRef.current = nextPage;
        setHasMore(nextPage * perPage < total);
      })
      .catch(() => setHasMore(false))
      .finally(() => setIsFetchingNextPage(false));
  }, [isFetchingFirstPage, isFetchingNextPage, hasMore, catalogNumber, perPage, fetchPage]);

  const loading = isFetchingFirstPage || isFetchingNextPage;
  return { hasMore, fetchNextPage, loading, isFetchingFirstPage, isFetchingNextPage };
};

export default useFetchPaginatedDraftCatalogProducts;
